import React, { useState } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSearchableDropdown from "@ui-components/Dropdown/GenericSearchableDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import SegmentedControl from "@ui-components/SegmentedContol/GenericSegmentedContol";
import { ReactComponent as IconDelete } from "@shared/assets/Icons/IconDelete_KE.svg";
import { type OutboundRuleFormData, type SubnetConfigMode, DYNAMIC_TAG_MIN_VERSION } from "../../../../../helpers/cloudPropagationHelpers";
import type { CloudElementTagKeyType } from "../../../request/request";
import TagValuePairList from "./TagValuePairList";

const DYNAMIC_TAG_VERSION_TOOLTIP = `Requires reflector version ${DYNAMIC_TAG_MIN_VERSION} or later`;

// "Subnet tags" and "Routing table" require dynamic tag support (CTX-163); disabled below DYNAMIC_TAG_MIN_VERSION.
const getSegmentedOptions = (disableDynamicTagOptions: boolean) => [
  { value: "manual", label: "Manual selection" },
  { value: "subnet_tags", label: "Subnet tags", disabled: disableDynamicTagOptions, disabledTooltip: disableDynamicTagOptions ? DYNAMIC_TAG_VERSION_TOOLTIP : undefined },
  { value: "routing_table", label: "Routing table", disabled: disableDynamicTagOptions, disabledTooltip: disableDynamicTagOptions ? DYNAMIC_TAG_VERSION_TOOLTIP : undefined },
];

export interface OutboundRuleCardProps {
  ruleIndex: number;
  rule: OutboundRuleFormData;
  vpcOptions: Array<{ value: string; label: string }>;
  subnetOptions: string[];
  isLoadingVpcs: boolean;
  isLoadingSubnets: boolean;
  subnetTagOptions: Array<{ value: string; label: string }>;
  isLoadingSubnetTagKeys: boolean;
  routeTableTagOptions: Array<{ value: string; label: string }>;
  isLoadingRouteTableTagKeys: boolean;
  subnetToAdd: string;
  onSubnetToAddChange: (value: string) => void;
  onAddSubnet: (subnet: string) => void;
  onRemoveSubnet: (subnet: string) => void;
  bgpCommunityError: string;
  nexthopIPError: string;
  onRuleChange: (rule: OutboundRuleFormData) => void;
  onDelete: () => void;
  canDelete: boolean;
  isDeleting?: boolean;
  onBgpCommunityChange: (value: string) => void;
  onNexthopIPChange: (value: string) => void;
  onTagKeysNeeded: (elementType: CloudElementTagKeyType) => void;
  // True when the associated reflector (standalone) or any instance in the cluster (cluster mode)
  // is below DYNAMIC_TAG_MIN_VERSION — disables "Subnet tags" / "Routing table" (CTX-163).
  disableDynamicTagOptions?: boolean;
}

const OutboundRuleCard: React.FC<OutboundRuleCardProps> = ({
  ruleIndex,
  rule,
  vpcOptions,
  subnetOptions,
  isLoadingVpcs,
  isLoadingSubnets,
  subnetTagOptions,
  isLoadingSubnetTagKeys,
  routeTableTagOptions,
  isLoadingRouteTableTagKeys,
  subnetToAdd,
  onAddSubnet,
  onRemoveSubnet,
  bgpCommunityError,
  nexthopIPError,
  onRuleChange,
  onDelete,
  canDelete,
  isDeleting,
  onBgpCommunityChange,
  onNexthopIPChange,
  onTagKeysNeeded,
  disableDynamicTagOptions = false,
}) => {
  const [subnetDropdownValue, setSubnetDropdownValue] = useState(subnetToAdd);
  const headerLabel = rule.name?.trim() || `Rule ${ruleIndex + 1}`;

  React.useEffect(() => {
    setSubnetDropdownValue(subnetToAdd);
  }, [subnetToAdd]);

  const patch = (partial: Partial<OutboundRuleFormData>) => {
    onRuleChange({ ...rule, ...partial });
  };

  const handleModeChange = (mode: string | number) => {
    const subnetConfigMode = mode as SubnetConfigMode;
    const updates: Partial<OutboundRuleFormData> = { subnetConfigMode };
    if (subnetConfigMode === "manual") {
      updates.subnetTags = [{ tagName: "", tagValue: "" }];
    } else if (subnetConfigMode === "subnet_tags") {
      updates.subnets = [];
    } else if (subnetConfigMode === "routing_table") {
      updates.subnets = [];
    }
    patch(updates);
  };

  React.useEffect(() => {
    if (rule.subnetConfigMode === "subnet_tags") {
      onTagKeysNeeded("subnet");
    } else if (rule.subnetConfigMode === "routing_table") {
      onTagKeysNeeded("route_table");
    }
  }, [rule.subnetConfigMode, onTagKeysNeeded]);

  React.useEffect(() => {
    if (rule.subnetConfigMode === "routing_table" && rule.filterSubnetsInRoutingTables) {
      onTagKeysNeeded("subnet");
    }
  }, [rule.subnetConfigMode, rule.filterSubnetsInRoutingTables, onTagKeysNeeded]);

  const filteredSubnetOptions = subnetOptions.filter((opt) => {
    if (rule.subnets.includes(opt)) return false;
    const optId = opt.split(" ")[0];
    return !rule.subnets.some((s) => s === optId || s.split(" ")[0] === optId);
  });

  const bodyContent = (
    <div className="p-4 flex flex-col gap-4">
      <GenericInput
        label="Rule name"
        value={rule.name ?? ""}
        onChange={(v) => patch({ name: v })}
        placeholder={`Rule ${ruleIndex + 1}`}
        className="text-body text-semantic-text-primary"
      />
      <div>
        <div className="text-semantic-text-primary mb-2 text-body">VPC</div>
        <GenericSearchableDropdown
          items={vpcOptions || []}
          value={String(rule.selectedVpc ?? "")}
          onSelect={(v) => patch({ selectedVpc: v })}
          defaultLabel="Select VPC..."
          widthClass="w-full"
          className="w-full"
          minWidthClass=""
          maxWidthClass=""
          disabled={isLoadingVpcs}
          emptyItemsMessage={isLoadingVpcs ? "Loading..." : "No VPCs available"}
          allowCustomValues={true}
        />
      </div>

      <div>
        <div className="text-semantic-text-primary mb-2 text-body">Subnet configuration</div>
        <SegmentedControl
          options={getSegmentedOptions(disableDynamicTagOptions)}
          value={rule.subnetConfigMode}
          onChange={handleModeChange}
          size="small"
          maxWidthClass="max-w-full"
          className="mb-4 [&>div:last-child]:border-semantic-border-secondary [&>div:last-child]:bg-semantic-bg-secondary [&_button]:text-semantic-text-secondary [&_button.bg-white]:bg-semantic-bg-primary [&_button.bg-white]:text-semantic-text-primary [&_button.bg-white]:border-semantic-border-secondary"
        />

        {rule.subnetConfigMode === "manual" && (
          <div>
            <div className="w-full mb-2">
              <GenericSearchableDropdown
                items={filteredSubnetOptions}
                value={subnetDropdownValue}
                onSelect={(v) => {
                  onAddSubnet(v);
                  setSubnetDropdownValue("");
                }}
                defaultLabel="Select..."
                widthClass="w-full"
                className="w-full"
                minWidthClass=""
                maxWidthClass=""
                disabled={isLoadingSubnets}
                emptyItemsMessage={isLoadingSubnets ? "Loading..." : "No subnets available"}
                allowCustomValues={true}
              />
            </div>
            {rule.subnets.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {rule.subnets.map((subnet, idx) => (
                  <GenericTag
                    key={idx}
                    label={subnet}
                    variant="default"
                    removable
                    onRemove={() => onRemoveSubnet(subnet)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {rule.subnetConfigMode === "subnet_tags" && (
          <TagValuePairList
            label="Subnet TAG:Value"
            required
            helperText="Select subnets matching all specified TAG:Value pairs."
            pairs={rule.subnetTags}
            onChange={(subnetTags) => patch({ subnetTags })}
            tagOptions={subnetTagOptions}
            isLoadingTags={isLoadingSubnetTagKeys}
          />
        )}

        {rule.subnetConfigMode === "routing_table" && (
          <div className="flex flex-col gap-4 w-full">
            <TagValuePairList
              label="Routing table TAG:Value"
              required
              helperText="Routing tables matching all TAG:Value pairs will be selected first."
              pairs={rule.routingTableTags}
              onChange={(routingTableTags) => patch({ routingTableTags })}
              tagOptions={routeTableTagOptions}
              isLoadingTags={isLoadingRouteTableTagKeys}
            />
            <GenericCheckbox
              label="Also filter subnets within selected routing tables"
              checked={rule.filterSubnetsInRoutingTables}
              onChange={(checked) =>
                patch({
                  filterSubnetsInRoutingTables: checked,
                  subnetFilterTags: checked
                    ? rule.subnetFilterTags
                    : [{ tagName: "", tagValue: "" }],
                })
              }
              size="sm"
              fullWidth
              className="items-start"
            />
            {rule.filterSubnetsInRoutingTables && (
              <TagValuePairList
                label="Subnet TAG:Value"
                helperText="Narrow down subnets using TAG:Value pairs (AND logic)."
                pairs={rule.subnetFilterTags}
                onChange={(subnetFilterTags) => patch({ subnetFilterTags })}
                tagOptions={subnetTagOptions}
                isLoadingTags={isLoadingSubnetTagKeys}
              />
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GenericInput
          label="BGP community"
          value={rule.bgpCommunity || ""}
          onChange={onBgpCommunityChange}
          placeholder="E.g. 65535:65535"
          helperText={bgpCommunityError || "Community for advertised subnets"}
          hasError={!!bgpCommunityError}
          className="text-body"
        />
        <GenericInput
          label="Next-hop IP address"
          value={rule.nexthopIPAddress || ""}
          onChange={onNexthopIPChange}
          placeholder="E.g. 192.168.1.1"
          helperText={nexthopIPError || "Optional"}
          hasError={!!nexthopIPError}
          className="text-body"
        />
      </div>
    </div>
  );

  return (
    <GenericCard
      variant="primary"
      shadow="flat"
      isCollapsible
      hasBorder={true}
      title={headerLabel}
      headerContent={headerLabel}
      bodyContent={bodyContent}
      initialExpanded={false}
      rightContent={
        canDelete ? (
          <GenericButton
            isIconOnly
            icon={<IconDelete className="w-4 h-4" />}
            iconPosition="right"
            size="sm"
            variant="danger"
            shape="filled"
            onClick={onDelete}
            disabled={isDeleting}
            loading={isDeleting}
          />
        ) : undefined
      }
      actionsContent={true}
    />
  );
};

export default OutboundRuleCard;
